import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../../../libs'
import { parseDecimal } from '../../../../../libs/utils'
import { DividendForm, DividendRequest } from './types'
import { initialDividendModalState } from './const'

const endpointFor = (operationType: string): string =>
  operationType === 'lp_investment' ? '/finance/lp_investment' : '/finance/dividend'

const buildBody = (data: DividendForm) => ({
  fromWallet: String(data.fromWallet ?? ''),
  walletId: String(data.walletId ?? data.fromWallet ?? ''),
  toWallet: data.toWallet,
  product: data.product,
  operationType: data.operationType,
  sentAmount: parseDecimal(data.sentAmount),
  sentCurrency: data.sentCurrency,
  receivedAmount: parseDecimal(data.receivedAmount),
  receivedCurrency: data.receivedCurrency,
  sentCommission: parseDecimal(data.sentCommission),
  sentCommissionCurrency: data.sentCommissionCurrency,
  receivedCommission: parseDecimal(data.receivedCommission),
  receivedCommissionCurrency: data.receivedCommissionCurrency,
  sentExchangeRate: parseDecimal(data.sentExchangeRate),
  receivedExchangeRate: parseDecimal(data.receivedExchangeRate),
  comment: data.comment,
  dateTime: data.dateTime,
})

export const addDividendMounted = createEvent<DividendForm>()
export const editDividendPaymentMounted = createEvent<DividendForm>()
export const deleteDividendPaymentMounted = createEvent<DividendForm>()
export const getDividendPaymentByIdMounted = createEvent<{ id: string }>()
export const setDividendForm = createEvent<DividendForm>()

export const $dividendForm = restore(setDividendForm, initialDividendModalState)

export const addDividend = fx<ReturnType<typeof buildBody>, void>({
  method: 'POST',
  url: `/finance/dividend`,
})

export const editDividendPayment = fx<ReturnType<typeof buildBody>, void>({
  method: 'PUT',
  url: `/finance/dividend/{id}`,
})

export const getDividendPaymentById = fx<{ id: string }, DividendRequest>({
  method: 'GET',
  url: `/finance/dividend/{id}`,
})

export const deleteDividendPayment = fx<DividendForm, void>({
  method: 'DELETE',
  url: `/finance/dividend/{id}`,
})

sample({
  clock: addDividendMounted,
  fn: (data) => ({
    url: endpointFor(data.operationType),
    body: buildBody(data),
  }),
  target: addDividend,
})

sample({
  clock: editDividendPaymentMounted,
  fn: (data) => ({
    url: `${endpointFor(data.operationType)}/${data.id}`,
    body: buildBody(data),
  }),
  target: editDividendPayment,
})

sample({
  clock: deleteDividendPaymentMounted,
  fn: (data) => ({
    url: `${endpointFor(data.operationType)}/${data.id}`,
  }),
  target: deleteDividendPayment,
})

sample({
  clock: getDividendPaymentByIdMounted,
  fn: ({ id }) => ({ url: `/finance/dividend/${id}` }),
  target: getDividendPaymentById,
})

sample({
  clock: getDividendPaymentById.doneData,
  fn: (doneData) => ({
    product: doneData.body.product,
    fromWallet: String(doneData.body.walletId),
    sentAmount: String(doneData.body.sentAmount),
    sentCurrency: doneData.body.sentCurrency,
    sentCommission: String(doneData.body.sentCommission),
    sentCommissionCurrency: doneData.body.sentCommissionCurrency ?? '',
    sentExchangeRate: String(doneData.body.sentExchangeRate),
    receivedAmount: String(doneData.body.receivedAmount),
    receivedCurrency: doneData.body.receivedCurrency,
    receivedCommission: String(doneData.body.receivedCommission),
    receivedCommissionCurrency: doneData.body.receivedCommissionCurrency ?? '',
    receivedExchangeRate: String(doneData.body.receivedExchangeRate),
    dateTime: doneData.body.dateTime,
    comment: doneData.body.comment,
    toWallet: doneData.body.toWallet,
    operationType: doneData.body.operationType,
    id: String(doneData.body.id),
    walletId: String(doneData.body.walletId),
  }),
  target: setDividendForm,
})
